/* eslint-disable @typescript-eslint/no-magic-numbers */
import useLatex from 'hooks/useLatex';
import useDismissableDialog from 'hooks/util/useDismissableDialog';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { cn } from 'utils';

export interface ButtonDialogProperties {
  open: boolean;
  title?: string;
  subtitle?: string;
  onLeftButtonClick?: () => void;
  onRightButtonClick?: () => void;
  leftButtonText?: string;
  rightButtonText?: string;
  onClose?: () => void;
  variant?: string;
}

export type ButtonDialogBoxContextType = {
  open: boolean;
  closeDialog: () => void;
  showButtonDialogBox: (dialogProperties: ButtonDialogProperties) => void;
  updateButtonDialogProperties: (dialogProperties: ButtonDialogProperties) => void;
};

const ButtonDialogBoxContext = createContext<ButtonDialogBoxContextType | undefined>(undefined);

export const useButtonDialogBox = () => {
  const context = useContext(ButtonDialogBoxContext);
  if (!context) {
    throw new Error('Dialog must be used within a DialogBoxProvider');
  }
  return context;
};

export function ButtonDialogBoxProvider({ children }: { children: ReactNode }) {
  const [buttonDialogProperties, setButtonDialogProperties] = useState<ButtonDialogProperties>({
    open: false,
  });

  const {
    open,
    title,
    subtitle,
    onLeftButtonClick,
    leftButtonText,
    onRightButtonClick,
    rightButtonText,
    variant = 'center',
  } = buttonDialogProperties;

  const onClose = useCallback(() => {
    if (buttonDialogProperties.onClose) {
      buttonDialogProperties.onClose();
    }
    setButtonDialogProperties((previous) => ({ ...previous, open: false }));
  }, [buttonDialogProperties]);

  const { dialogReference, onDismiss } = useDismissableDialog({
    isOpen: open,
    onClose,
    dismissable: true,
  });
  useLatex(title || '');
  useLatex(subtitle || '');

  const showButtonDialogBox = useCallback((dialogProperties: ButtonDialogProperties) => {
    setButtonDialogProperties(dialogProperties);
  }, []);

  const value = useMemo(
    () => ({
      open,
      showButtonDialogBox,
      updateButtonDialogProperties: setButtonDialogProperties,
      closeDialog: onDismiss,
    }),
    [onDismiss, showButtonDialogBox, open],
  );

  return (
    <ButtonDialogBoxContext.Provider value={value}>
      {children}
      {open && (
        <div className="absolute top-0 z-20 flex h-[100vh] w-[100vw] items-center backdrop-blur-sm backdrop-brightness-50">
          <dialog
            ref={dialogReference}
            open={open}
            className="relative flex max-w-[665px] flex-col items-center justify-center rounded-xl bg-neutral-white"
          >
            <RxCross2
              className="absolute right-6 top-6 z-20 cursor-pointer"
              style={{ color: '#4F4F4F', fontSize: '2em' }}
              onClick={onDismiss}
            />
            <div className="flex h-full min-h-[15em] w-full min-w-[35em] flex-col items-center justify-center py-12 pl-12 pr-16">
              <div className="custom-scrollbar flex h-full max-h-52 w-full flex-col items-center justify-start overflow-y-auto">
                {title && (
                  <p
                    className="w-full text-center text-[1.5625em] font-semibold leading-normal text-primary-blue-3"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                )}
                {subtitle && (
                  <p
                    className={cn('max-w-full pr-4 text-center text-l1 font-light leading-normal', {
                      'mt-4': title,
                      'text-left': variant === 'left',
                    })}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: subtitle }}
                  />
                )}
              </div>
              <div className="mt-6 flex w-full justify-center">
                {onLeftButtonClick && leftButtonText && (
                  <button
                    type="button"
                    onClick={onLeftButtonClick}
                    className="font-boldshadow-md bottom-6 z-0 w-[238px] rounded-[72px] border-2 border-primary-blue-3 px-4 py-4 text-l1 font-semibold text-neutral-grey-4 shadow-custom"
                  >
                    {leftButtonText}
                  </button>
                )}
                {onRightButtonClick && rightButtonText && (
                  <button
                    type="button"
                    onClick={onRightButtonClick}
                    className="font-boldshadow-md bottom-6 z-0 ml-7 w-[238px] rounded-[72px] bg-primary-yellow-3 px-4 py-4 text-l1 font-semibold text-primary-blue-3 shadow-custom"
                  >
                    {rightButtonText}
                  </button>
                )}
              </div>
            </div>
          </dialog>
        </div>
      )}
    </ButtonDialogBoxContext.Provider>
  );
}
